// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

export const CHECKOUT_STEPS = {
    CART: 0,
    ADDRESS: 1,
    REVIEW: 2,
    CONFIRMATION: 3,
};

export interface CheckoutStep {
    /** Step Name */
    name: string;
    /** Step Name */
    displayName?: string;
    /** Step index */
    index: number;
    /** Step Next */
    next?: number;
    /** Step Prev */
    prev: number;
}

/**
 * Cases:
 * 1. isPickUp = false & canSubmitForApproval = false then show 4 steps.
 * 2. isPickUp = true & canSubmitForApproval = false then hide Shipping only.
 * 2. isPickUp = false & canSubmitForApproval = true then hide Payment only.
 * 2. isPickUp = true & canSubmitForApproval = true then hide Payment & Shipping.
 * @param isPickUp boolean
 * @param requiresApproval boolean
 * @returns CheckoutStep[]
 */
export const generateSteps = (isPickUp: boolean, requiresApproval?: boolean): CheckoutStep[] => {
    // initialize steps with addresses and review
    const steps: CheckoutStep[] = [
        {
            name: "Addresses",
            prev: CHECKOUT_STEPS.CART,
            index: CHECKOUT_STEPS.ADDRESS,
            next: CHECKOUT_STEPS.REVIEW,
        },
        {
            name: "Review",
            displayName: "Review & Pay",
            prev: CHECKOUT_STEPS.ADDRESS,
            index: CHECKOUT_STEPS.REVIEW,
        },
        {
            name: "Confirmation",
            displayName: "Confirmation",
            prev: CHECKOUT_STEPS.REVIEW,
            index: CHECKOUT_STEPS.CONFIRMATION,
        },
    ];

    return steps;
};

export const findStep = (steps: CheckoutStep[], currentStep: number) => {
    return steps.find(item => item.index === currentStep) || steps[0] || {};
};

export const nextStepHandler = (steps: CheckoutStep[], currentStep: number) => {
    const next = findStep(steps, currentStep).next;
    return findStep(steps, next || 1);
};

export const previousStepHandler = (steps: CheckoutStep[], currentStep: number) => {
    const prev = findStep(steps, currentStep).prev;
    return findStep(steps, prev);
};

type CallBackSome<T> = (value: T, index?: number, collection?: T[]) => Promise<boolean>;

export async function some<T>(elements: T[], cb: CallBackSome<T>): Promise<boolean> {
    for (const [index, element] of elements.entries()) {
        if (await cb(element, index, elements)) {
            return true;
        }
    }

    return false;
}
